import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "../axios";
import { useThemePreview } from "../themePreview";
import { CarCarousel, LunchBanner, showDate, todayKey } from "../components/ShowPieces";
import SunsetHero from "../components/SunsetHero";
import EnterYourCar from "../components/EnterYourCar";
import WinnerPhotoCard from "../components/WinnerPhotoCard";

const PARK_LAWN = {
  id: "park",
  display_url: "/images/events/hbc-central-park-001.jpg",
  thumb_url: "/images/events/hbc-central-park-001.jpg",
};

const LIBRARY_MAP =
  "https://maps.google.com/maps?q=Huntington+Beach+Public+Library,+7111+Talbert+Avenue,+Huntington+Beach,+CA+92648&hl=en&ll=33.701199,-118.003988&z=15";

const LIBRARY_EMBED =
  "https://maps.google.com/maps?q=33.701199,-118.003988&z=15&hl=en&output=embed";

const LIBRARY_MAP_LABEL = "View map of Huntington Beach Library, 7111 Talbert Avenue";

const CONCOURS_VIDEO =
  "https://drive.google.com/file/d/1_HAJIZ8CUVxUKsjI4bkvqkWwuNOLn1fa/view?ts=698cce11";

const CONCOURS_VIDEO_LABEL =
  "Click here to see a brief video of Bart McGrath, Huntington Beach Concours d'Elegance founder talking about our event held at the Huntington Beach Central Library.";

function MapPin() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 2.8a6.2 6.2 0 0 0-6.2 6.2c0 4.6 6.2 12.2 6.2 12.2s6.2-7.6 6.2-12.2A6.2 6.2 0 0 0 12 2.8zm0 8.4a2.2 2.2 0 1 1 0-4.4 2.2 2.2 0 0 1 0 4.4z"
      />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M10 8.2v7.6l6.4-3.8L10 8.2z" fill="currentColor" />
    </svg>
  );
}

function LawnBlock() {
  const [photos, setPhotos] = useState([]);
  const [index, setIndex] = useState(0);
  const slides = photos.length ? photos : [PARK_LAWN];
  const current = index % slides.length;

  useEffect(() => {
    axios
      .get("/api/users/lawn-photos/")
      .then(({ data }) => setPhotos(data))
      .catch(() => setPhotos([]));
  }, []);

  useEffect(() => {
    if (slides.length < 2) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setIndex((value) => (value + 1) % slides.length);
    }, 8000);
    return () => window.clearInterval(timer);
  }, [slides.length, current]);

  return (
    <section className="site-section">
      <div className="site-wrap">
        <div className="split lawn-split">
          <div className="lawn-gallery">
            <div className="lawn-frame">
              {slides.map((photo, photoIndex) => (
                <img
                  key={photo.id}
                  className={photoIndex === current ? "is-on" : ""}
                  src={photo.display_url}
                  alt={photoIndex === current ? "The lawn at Huntington Beach Central Park" : ""}
                />
              ))}
            </div>
            {photos.length > 0 ? (
              <div className="lawn-strip" aria-label="Lawn photos">
                {photos.map((photo, photoIndex) => (
                  <button
                    key={photo.id}
                    type="button"
                    className={photoIndex === current ? "is-on" : ""}
                    aria-label={`Show lawn photo ${photoIndex + 1}`}
                    aria-current={photoIndex === current ? "true" : undefined}
                    onClick={() => setIndex(photoIndex)}
                  >
                    <img src={photo.thumb_url || photo.display_url} alt="" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <article className="lawn-card">
            <p className="site-kicker">Since 1986</p>
            <h2>On the lawn at the library</h2>
            <div className="hero-actions">
              <Link className="btn btn-site-quiet" to="/company-information?section=purpose">
                Our Purpose
              </Link>
              <Link className="btn btn-site-quiet" to="/company-information?section=history">
                Our History
              </Link>
              <Link className="btn btn-site-quiet" to="/company-information?section=committee">
                Our Committee
              </Link>
            </div>
            <p>
              The show lives on the grass at Huntington Beach Central Park, next to the Public Library and Cultural
              Resource Center at Talbert and Goldenwest.
            </p>
            <div className="lawn-place">
              <div className="lawn-address">
                <a
                  className="lawn-address-link"
                  href={LIBRARY_MAP}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Huntington Beach Library, 7111 Talbert Ave., Huntington Beach, CA 92648"
                >
                  <MapPin />
                  <span>
                    Huntington Beach Library
                    <br />
                    7111 Talbert Ave.
                    <br />
                    Huntington Beach, CA 92648
                  </span>
                </a>
              </div>
              <div className="square-map">
                <iframe
                  title="Map of Huntington Beach Public Library, 7111 Talbert Avenue"
                  src={LIBRARY_EMBED}
                  loading="lazy"
                  tabIndex={-1}
                  aria-hidden="true"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <a
                  className="square-map-open"
                  href={LIBRARY_MAP}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={LIBRARY_MAP_LABEL}
                  title={LIBRARY_MAP_LABEL}
                >
                  <span>
                    <MapPin />
                    View map
                  </span>
                </a>
              </div>
            </div>
            <footer className="lawn-card-foot">
              <h3>2026 Concours Video</h3>
              <a
                className="lawn-video-link"
                href={CONCOURS_VIDEO}
                target="_blank"
                rel="noopener noreferrer"
                title={CONCOURS_VIDEO_LABEL}
              >
                <PlayIcon />
                <span>{CONCOURS_VIDEO_LABEL}</span>
              </a>
            </footer>
          </article>
        </div>
      </div>
    </section>
  );
}

function countWord(count, singular, plural) {
  return count === 1 ? singular : plural;
}

function dayPhotos(event, fallback) {
  const urls = (event?.photos || [])
    .filter((photo) => photo.on_home && photo.image)
    .map((photo) => photo.image);
  if (urls.length) {
    return urls;
  }
  if (!event && fallback) {
    return [fallback];
  }
  return [];
}

function DayPhotos({ images, name }) {
  const [index, setIndex] = useState(0);
  const count = images.length;
  const many = count > 1;
  const go = (event, next) => {
    event.preventDefault();
    event.stopPropagation();
    setIndex((next + count) % count);
  };

  if (!count) {
    return null;
  }

  return (
    <div className={many ? "day-card-photo is-carousel" : "day-card-photo"}>
      {images.map((src, itemIndex) => (
        <img key={`${src}-${itemIndex}`} src={src} alt="" className={itemIndex === index ? "is-on" : ""} />
      ))}
      {many ? (
        <>
          <button
            type="button"
            className="day-photo-arrow day-photo-prev"
            aria-label={`Previous photo of ${name}`}
            title={`Previous photo of ${name}`}
            onClick={(event) => go(event, index - 1)}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d="M10 3 L5 8 L10 13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            className="day-photo-arrow day-photo-next"
            aria-label={`Next photo of ${name}`}
            title={`Next photo of ${name}`}
            onClick={(event) => go(event, index + 1)}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d="M6 3 L11 8 L6 13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className="day-photo-dots">
            {images.map((src, itemIndex) => (
              <button
                key={`${src}-dot-${itemIndex}`}
                type="button"
                className={itemIndex === index ? "is-on" : ""}
                aria-label={`Photo ${itemIndex + 1} of ${count}`}
                aria-current={itemIndex === index ? "true" : undefined}
                title={`Photo ${itemIndex + 1} of ${count}`}
                onClick={(event) => go(event, itemIndex)}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function DayCard({ label, event, fallback }) {
  const cars = event?.car_count || 0;
  const classes = event?.class_count || 0;
  const showLabel = event ? `View Show, ${event.title}` : "";
  const photos = dayPhotos(event, fallback);

  return (
    <article className="day-card">
      <div className="day-card-top">
        <p className="site-kicker day-card-when">{event ? showDate(event.date) : label}</p>
        <h3>{event ? event.title : "Event coming soon."}</h3>
      </div>
      <DayPhotos images={photos} name={event ? event.title : label} />
      {event ? (
        <ul className="day-facts">
          <li>
            <strong>{cars}</strong>
            <span>{countWord(cars, "Car registered", "Cars registered")}</span>
          </li>
          <li>
            <strong>{classes}</strong>
            <span>{countWord(classes, "Class", "Classes")}</span>
          </li>
          <li aria-label="Trophies coming soon">
            <strong>Soon</strong>
            <span>Trophies</span>
          </li>
        </ul>
      ) : null}
      {event ? (
        <Link className="btn btn-site day-show" to={`/show/${event.id}`} aria-label={showLabel} title={showLabel}>
          View Show
        </Link>
      ) : null}
    </article>
  );
}

function monthDay(value) {
  if (!value) {
    return "";
  }
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function HeroDay({ label, event }) {
  const showLabel = event ? `View the show, ${event.title}` : "";
  return (
    <article>
      <h3>{event ? event.title : "Details coming soon."}</h3>
      <p className="hero-day-line">
        <span className="site-kicker">{label}</span>
        {event ? <span className="hero-dates">{monthDay(event.date)}</span> : null}
      </p>
      {event ? (
        <Link className="hero-show-link" to={`/show/${event.id}`} aria-label={showLabel} title={showLabel}>
          View the show
        </Link>
      ) : null}
    </article>
  );
}

function TwoDays({ images, days }) {
  return (
    <section className="site-section two-days">
      <div className="site-wrap">
        <header className="two-days-head">
          <p className="site-kicker">HB Central Park Show</p>
          <h2>One park, two different mornings</h2>
        </header>
        <div className="day-grid">
          <DayCard label="Saturday" event={days?.saturday || null} fallback={images.saturday} />
          <DayCard label="Sunday" event={days?.sunday || null} fallback={images.sunday} />
        </div>
      </div>
    </section>
  );
}

function HomeScreen() {
  const [winners, setWinners] = useState([]);
  const [events, setEvents] = useState([]);
  const [days, setDays] = useState(null);
  const [cars, setCars] = useState([]);
  const { images, template } = useThemePreview();
  const today = todayKey();
  const nextEvent = events.find((item) => item.date >= today) || null;

  useEffect(() => {
    axios
      .get("/api/users/results/")
      .then(({ data }) => setWinners(data))
      .catch(() => setWinners([]));
    axios
      .get("/api/users/events/")
      .then(({ data }) => setEvents(data))
      .catch(() => setEvents([]));
    axios
      .get("/api/users/home/")
      .then(({ data }) =>
        setDays({ saturday: data.saturday || null, sunday: data.sunday || null })
      )
      .catch(() => setDays({ saturday: null, sunday: null }));
    axios
      .get("/api/users/showcase/")
      .then(({ data }) => setCars(data.filter((car) => car.image)))
      .catch(() => setCars([]));
  }, []);

  return (
    <div className="beach">
      <section
        className="hero"
        style={
          template === "sand"
            ? undefined
            : { backgroundImage: `url(${images.hero})`, "--hero-image": `url(${images.hero})` }
        }
      >
        {template === "sand" ? <SunsetHero /> : null}
        <div className="hero-copy">
          <p className="site-kicker">Huntington Beach Central Park</p>
          <h1>Concours d'Elegance</h1>
          <div className="hero-event">
            <p className="site-kicker">Upcoming event</p>
            <h2>HB Central Park Show</h2>
            {days ? (
              <div className="hero-days">
                <HeroDay label="Saturday" event={days.saturday} />
                <HeroDay label="Sunday" event={days.sunday} />
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <LawnBlock />

      <section className="site-section winner-band">
        <div className="site-wrap">
          <p className="site-kicker">After judging</p>
          <h2>Class winners</h2>
          {winners.length === 0 ? (
            <p>Winners will be posted here once a class has been called.</p>
          ) : (
            <div className="winner-grid">
              {winners.map((item) => (
                <WinnerPhotoCard key={item.id} item={item} />
              ))}
            </div>
          )}
          <Link className="text-link" to="/awards">
            Awards and photos
          </Link>
        </div>
      </section>

      <TwoDays images={images} days={days} />

      <EnterYourCar />

      {cars.length > 0 && (
        <section className="site-section">
          <div className="site-wrap">
            <p className="site-kicker">From the garages</p>
            <div className="member-cars-head">
              <h2>Member cars</h2>
              <p>Click a car to see more</p>
            </div>
          </div>
          <CarCarousel cars={cars} />
        </section>
      )}

      <section className="site-section site-section-sand">
        <div className="site-wrap">
          <LunchBanner lunch={nextEvent?.lunches?.[0]} />
        </div>
      </section>

    </div>
  );
}

export default HomeScreen;
